import { Injectable } from '@angular/core';
import { NgxDatatableColumn } from '../../interfaces/ui/ngx-datatable.interface';
import {
  MobileCardAction,
  ActionColumnDetectionResult,
  ActionColumnCriteria,
  ActionDetectionContext,
  MobileActionDetectionOptions,
  ActionDetectionStrategy,
  ExpandableContentDetectionResult,
  ExpandableContentOptions,
  ExpandableContentCriteria,
  ExpandableDetectionStrategy,
  ExpandableDetectionContext,
  ExpandableContentType,
  VisibleColumnDetectionResult,
  VisibleColumnOptions,
  VisibleColumnCriteria,
  VisibleDetectionStrategy,
  VisibleDetectionContext,
} from '../../interfaces/ui/ngx-datatable-mobile.interface';
import {
  ACTION_COLUMN_PATTERNS,
  DETECTION_STRATEGIES,
  CRITERIA_WEIGHTS,
  DEFAULT_DETECTION_OPTIONS,
  PATTERN_MATCHERS,
  CONFIDENCE_THRESHOLDS,
  DETECTION_METHOD_PRIORITY,
  EXPANDABLE_CONTENT_CONFIGS,
  EXPANDABLE_DETECTION_STRATEGIES,
  EXPANDABLE_CRITERIA_WEIGHTS,
  DEFAULT_EXPANDABLE_OPTIONS,
  EXPANDABLE_CONFIDENCE_THRESHOLDS,
  EXPANDABLE_STRATEGY_PRIORITY,
  VISIBLE_COLUMN_CONFIGS,
  VISIBLE_DETECTION_STRATEGIES,
  VISIBLE_CRITERIA_WEIGHTS,
  DEFAULT_VISIBLE_OPTIONS,
  VISIBLE_CONFIDENCE_THRESHOLDS,
  VISIBLE_STRATEGY_PRIORITY,
} from '../../helpers/ui/ngx-datatable-mobile.constant';

@Injectable({
  providedIn: 'root',
})
export class NgxDatatableMobileService {
  detectActionColumns(
    columns: Partial<NgxDatatableColumn>[] | undefined,
    options: MobileActionDetectionOptions = DEFAULT_DETECTION_OPTIONS
  ): ActionColumnDetectionResult {
    if (!columns?.length || !options.enableAutoDetection) {
      return this.createEmptyResult();
    }

    const context: ActionDetectionContext = {
      columns,
      strategy: 'metadata-first',
      options,
    };

    return (
      this.tryMetadataDetection(context) ||
      this.tryNamePatternDetection(context) ||
      this.tryPositionBasedDetection(context) ||
      this.tryTemplateOnlyDetection(context) ||
      this.createEmptyResult()
    );
  }

  private tryMetadataDetection(
    context: ActionDetectionContext
  ): ActionColumnDetectionResult | null {
    const metadataColumns = context.columns.filter(
      (col) => col.frozenLeft && col.isMobileActions
    );

    return metadataColumns.length > 0
      ? {
          columns: metadataColumns,
          confidence: 1.0,
          detectionMethod: 'metadata',
        }
      : null;
  }

  private tryNamePatternDetection(
    context: ActionDetectionContext
  ): ActionColumnDetectionResult | null {
    const nameBasedColumns = context.columns.filter((col) => {
      const criteria = this.evaluateColumnCriteria(col, 0);
      return (
        criteria.nameMatches &&
        (!context.options.templateRequired || criteria.hasTemplate)
      );
    });

    if (nameBasedColumns.length === 0) return null;

    const confidence = this.calculateConfidence(nameBasedColumns[0], 0);
    return confidence >=
      (context.options.confidenceThreshold ?? CONFIDENCE_THRESHOLDS.MEDIUM)
      ? {
          columns: nameBasedColumns,
          confidence,
          detectionMethod: 'name',
        }
      : null;
  }

  private tryPositionBasedDetection(
    context: ActionDetectionContext
  ): ActionColumnDetectionResult | null {
    if (!context.options.positionBased) return null;

    const firstColumn = context.columns[0];
    if (!firstColumn?.cellTemplate) return null;

    const criteria = this.evaluateColumnCriteria(firstColumn, 0);
    const confidence = this.calculateConfidence(firstColumn, 0);

    return confidence >= CONFIDENCE_THRESHOLDS.LOW
      ? {
          columns: [firstColumn],
          confidence,
          detectionMethod: 'position',
        }
      : null;
  }

  private tryTemplateOnlyDetection(
    context: ActionDetectionContext
  ): ActionColumnDetectionResult | null {
    const templateColumns = context.columns.filter(
      (col) => col.cellTemplate && !col.name
    );

    return templateColumns.length > 0
      ? {
          columns: templateColumns.slice(0, 1),
          confidence: CONFIDENCE_THRESHOLDS.LOW,
          detectionMethod: 'template',
        }
      : null;
  }

  private evaluateColumnCriteria(
    column: Partial<NgxDatatableColumn>,
    index: number
  ): ActionColumnCriteria {
    const columnName =
      typeof column.name === 'string' ? column.name.toLowerCase() : '';

    return {
      hasName: !!column.name,
      hasTemplate: !!column.cellTemplate,
      nameMatches: columnName
        ? PATTERN_MATCHERS['includes'](columnName)
        : false,
      isFirstColumn: index === 0,
      isFrozenLeft: !!column.frozenLeft,
      isMobileActions: !!column.isMobileActions,
    };
  }

  private calculateConfidence(
    column: Partial<NgxDatatableColumn>,
    index: number
  ): number {
    const criteria = this.evaluateColumnCriteria(column, index);

    return (
      Object.entries(criteria).reduce((score, [key, value]) => {
        const weight = CRITERIA_WEIGHTS[key as keyof ActionColumnCriteria];
        return score + (value ? weight : 0);
      }, 0) / Object.keys(CRITERIA_WEIGHTS).length
    );
  }

  private createEmptyResult(): ActionColumnDetectionResult {
    return {
      columns: [],
      confidence: 0,
      detectionMethod: 'name',
    };
  }

  isActionColumn(columnName: string | undefined): boolean {
    return (
      !!columnName &&
      (PATTERN_MATCHERS['exact'](columnName) ||
        PATTERN_MATCHERS['includes'](columnName))
    );
  }

  getBestStrategy(
    columns: Partial<NgxDatatableColumn>[]
  ): ActionDetectionStrategy {
    const hasMetadata = columns.some(
      (col) => col.frozenLeft && col.isMobileActions
    );
    const hasNamedActions = columns.some((col) =>
      this.isActionColumn(col.name as string)
    );
    const hasFirstTemplate = columns[0]?.cellTemplate;

    return hasMetadata
      ? 'metadata-first'
      : hasNamedActions
      ? 'name-pattern'
      : hasFirstTemplate
      ? 'position-based'
      : 'template-only';
  }

  validateOptions(
    options?: Partial<MobileActionDetectionOptions>
  ): MobileActionDetectionOptions {
    return {
      ...DEFAULT_DETECTION_OPTIONS,
      ...options,
    };
  }

  getConfidenceLevel(
    confidence: number
  ): 'high' | 'medium' | 'low' | 'minimum' {
    return confidence >= CONFIDENCE_THRESHOLDS.HIGH
      ? 'high'
      : confidence >= CONFIDENCE_THRESHOLDS.MEDIUM
      ? 'medium'
      : confidence >= CONFIDENCE_THRESHOLDS.LOW
      ? 'low'
      : 'minimum';
  }

  hasActionColumns(
    columns: Partial<NgxDatatableColumn>[] | undefined
  ): boolean {
    if (!columns?.length) {
      return false;
    }
    return (
      columns.some((col) => !!(col.frozenLeft && col.isMobileActions)) ||
      columns.some((col) => this.isActionColumn(col.name as string)) ||
      !!(columns[0]?.cellTemplate && !columns[0]?.name)
    );
  }

  getDetectionSummary(columns: Partial<NgxDatatableColumn>[] | undefined): {
    hasActions: boolean;
    strategy: ActionDetectionStrategy;
    confidence: number;
    count: number;
  } {
    const detectionResult = this.detectActionColumns(columns);
    const strategy = columns?.length
      ? this.getBestStrategy(columns)
      : 'template-only';

    return {
      hasActions: detectionResult.columns.length > 0,
      strategy,
      confidence: detectionResult.confidence,
      count: detectionResult.columns.length,
    };
  }

  detectExpandableContent(
    columns: Partial<NgxDatatableColumn>[],
    row?: any,
    options?: Partial<ExpandableContentOptions>
  ): ExpandableContentDetectionResult {
    const mergedOptions = { ...DEFAULT_EXPANDABLE_OPTIONS, ...options };

    if (!mergedOptions.enableAutoDetection) {
      return this.createEmptyExpandableResult();
    }

    const templateResult = this.detectByTemplate(columns);
    if (templateResult.confidence >= mergedOptions.confidenceThreshold) {
      return templateResult;
    }

    const propertyResult = this.detectByExpandableProperty(columns);
    if (propertyResult.confidence >= mergedOptions.confidenceThreshold) {
      return propertyResult;
    }

    if (mergedOptions.checkRowData && row) {
      const dataResult = this.detectByRowData(columns, row);
      if (dataResult.confidence >= mergedOptions.confidenceThreshold) {
        return dataResult;
      }
    }

    return (
      [templateResult, propertyResult].sort(
        (a, b) => b.confidence - a.confidence
      )[0] || this.createEmptyExpandableResult()
    );
  }

  private detectByTemplate(
    columns: Partial<NgxDatatableColumn>[]
  ): ExpandableContentDetectionResult {
    const expandableColumns = columns.filter(
      (col) => !!(col.expandableRowTemplate || col.expandableCellTemplate)
    );

    const confidence = expandableColumns.length > 0 ? 1.0 : 0.0;
    const contentType: ExpandableContentType =
      expandableColumns.length > 0 ? 'template' : 'none';

    return {
      hasExpandableContent: expandableColumns.length > 0,
      expandableColumns,
      contentType,
      confidence,
      strategy: EXPANDABLE_DETECTION_STRATEGIES['TEMPLATE_FIRST'],
    };
  }

  private detectByExpandableProperty(
    columns: Partial<NgxDatatableColumn>[]
  ): ExpandableContentDetectionResult {
    const expandableColumns = columns.filter((col) => !!col.isExpandable);

    const confidence = expandableColumns.length > 0 ? 0.8 : 0.0;
    const contentType: ExpandableContentType =
      expandableColumns.length > 0 ? 'object' : 'none';

    return {
      hasExpandableContent: expandableColumns.length > 0,
      expandableColumns,
      contentType,
      confidence,
      strategy: EXPANDABLE_DETECTION_STRATEGIES['PROPERTY_FIRST'],
    };
  }

  private detectByRowData(
    columns: Partial<NgxDatatableColumn>[],
    row: any
  ): ExpandableContentDetectionResult {
    const criteria = this.evaluateExpandableCriteria(columns, row);
    const confidence = this.calculateExpandableConfidence(criteria);

    const contentType: ExpandableContentType = criteria.hasArrayData
      ? 'array'
      : criteria.hasObjectData
      ? 'object'
      : 'none';

    const expandableColumns = columns.filter((col) =>
      this.hasExpandableData(row, col.name as string)
    );

    return {
      hasExpandableContent: confidence > 0.3,
      expandableColumns,
      contentType,
      confidence,
      strategy: EXPANDABLE_DETECTION_STRATEGIES['DATA_DRIVEN'],
    };
  }

  private evaluateExpandableCriteria(
    columns: Partial<NgxDatatableColumn>[],
    row: any
  ): ExpandableContentCriteria {
    return {
      hasExpandableProperty: columns.some((col) => !!col.isExpandable),
      hasExpandableCellTemplate: columns.some(
        (col) => !!col.expandableCellTemplate
      ),
      hasExpandableRowTemplate: columns.some(
        (col) => !!col.expandableRowTemplate
      ),
      hasArrayData: this.hasArrayData(row),
      hasObjectData: this.hasObjectData(row),
    };
  }

  private calculateExpandableConfidence(
    criteria: ExpandableContentCriteria
  ): number {
    return Object.entries(criteria).reduce((total, [key, value]) => {
      const weight = EXPANDABLE_CRITERIA_WEIGHTS[key] || 0;
      return total + (value ? weight : 0);
    }, 0);
  }

  private hasArrayData(row: any): boolean {
    return Object.values(row || {}).some(
      (value) => Array.isArray(value) && value.length > 0
    );
  }

  private hasObjectData(row: any): boolean {
    return Object.values(row || {}).some(
      (value) =>
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
    );
  }

  private hasExpandableData(row: any, columnName: string): boolean {
    if (!row || !columnName) return false;

    const value = row[columnName];
    return (
      (Array.isArray(value) && value.length > 0) ||
      (value && typeof value === 'object' && Object.keys(value).length > 0)
    );
  }

  private createEmptyExpandableResult(): ExpandableContentDetectionResult {
    return {
      hasExpandableContent: false,
      expandableColumns: [],
      contentType: 'none',
      confidence: 0,
      strategy: EXPANDABLE_DETECTION_STRATEGIES['TEMPLATE_FIRST'],
    };
  }

  hasExpandableContent(
    columns: Partial<NgxDatatableColumn>[],
    row?: any,
    options?: Partial<ExpandableContentOptions>
  ): boolean {
    if (!columns?.length) {
      return false;
    }

    const hasExpandableColumns = columns.some(
      (col) =>
        !!(
          col.isExpandable ||
          col.expandableCellTemplate ||
          col.expandableRowTemplate
        )
    );

    if (hasExpandableColumns) {
      return true;
    }

    return false;
  }

  getExpandableDetectionSummary(
    columns: Partial<NgxDatatableColumn>[],
    row?: any,
    options?: Partial<ExpandableContentOptions>
  ) {
    const result = this.detectExpandableContent(columns, row, options);
    const hasExpandable = this.hasExpandableContent(columns, row, options);

    return {
      hasExpandableContent: hasExpandable,
      strategy: result.strategy,
      confidence: result.confidence,
      contentType: result.contentType,
      count: result.expandableColumns.length,
      confidenceLevel: this.getConfidenceLevel(result.confidence),
    };
  }

  detectVisibleColumns(
    columns: Partial<NgxDatatableColumn>[],
    options?: Partial<VisibleColumnOptions>
  ): VisibleColumnDetectionResult {
    const mergedOptions = { ...DEFAULT_VISIBLE_OPTIONS, ...options };

    if (!mergedOptions.enableAutoDetection) {
      return this.createDefaultVisibleResult(columns);
    }

    if (mergedOptions.respectVisibleProperty) {
      const propertyResult = this.detectByVisibleProperty(
        columns,
        mergedOptions
      );
      if (propertyResult.confidence >= mergedOptions.confidenceThreshold) {
        return propertyResult;
      }
    }

    const patternResult = this.detectByPatterns(columns, mergedOptions);
    if (patternResult.confidence >= mergedOptions.confidenceThreshold) {
      return patternResult;
    }

    const hybridResult = this.detectByHybridApproach(columns, mergedOptions);
    if (hybridResult.confidence >= mergedOptions.confidenceThreshold) {
      return hybridResult;
    }

    const results = [];
    if (mergedOptions.respectVisibleProperty) {
      const propertyResult = this.detectByVisibleProperty(
        columns,
        mergedOptions
      );
      results.push(propertyResult);
    }
    results.push(patternResult, hybridResult);

    return (
      results
        .filter((result) => result)
        .sort((a, b) => b.confidence - a.confidence)[0] ||
      this.createDefaultVisibleResult(columns)
    );
  }

  private detectByVisibleProperty(
    columns: Partial<NgxDatatableColumn>[],
    options: VisibleColumnOptions
  ): VisibleColumnDetectionResult {
    const visibleColumns = columns.filter((col) => {
      if (col.visible === false) return false;
      if (col.visible === true) return true;

      return this.passesBasicVisibilityChecks(col, options);
    });

    const hiddenColumns = columns.filter(
      (col) => !visibleColumns.includes(col)
    );
    const confidence = this.calculateVisibilityConfidence(
      columns,
      visibleColumns,
      options
    );

    return {
      visibleColumns,
      hiddenColumns,
      strategy: VISIBLE_DETECTION_STRATEGIES['PROPERTY_FIRST'],
      confidence,
      totalProcessed: columns.length,
    };
  }

  private detectByPatterns(
    columns: Partial<NgxDatatableColumn>[],
    options: VisibleColumnOptions
  ): VisibleColumnDetectionResult {
    const visibleColumns = columns.filter((col) => {
      if (!this.passesBasicVisibilityChecks(col, options)) return false;

      const criteria = this.evaluateVisibilityCriteria(col, options);
      return (
        !criteria.matchesExcludePattern &&
        !criteria.isActionColumn &&
        !criteria.isExpandableColumn
      );
    });

    const hiddenColumns = columns.filter(
      (col) => !visibleColumns.includes(col)
    );
    const confidence = this.calculateVisibilityConfidence(
      columns,
      visibleColumns,
      options
    );

    return {
      visibleColumns,
      hiddenColumns,
      strategy: VISIBLE_DETECTION_STRATEGIES['PATTERN_BASED'],
      confidence,
      totalProcessed: columns.length,
    };
  }

  private detectByHybridApproach(
    columns: Partial<NgxDatatableColumn>[],
    options: VisibleColumnOptions
  ): VisibleColumnDetectionResult {
    const visibleColumns = columns.filter((col) => {
      if (col.visible === false) return false;
      if (col.visible === true) return true;

      if (!this.passesBasicVisibilityChecks(col, options)) return false;

      const criteria = this.evaluateVisibilityCriteria(col, options);
      const score = this.calculateColumnVisibilityScore(criteria);

      return score > 0;
    });

    const hiddenColumns = columns.filter(
      (col) => !visibleColumns.includes(col)
    );
    const confidence = this.calculateVisibilityConfidence(
      columns,
      visibleColumns,
      options
    );

    return {
      visibleColumns,
      hiddenColumns,
      strategy: VISIBLE_DETECTION_STRATEGIES['HYBRID'],
      confidence,
      totalProcessed: columns.length,
    };
  }

  private passesBasicVisibilityChecks(
    column: Partial<NgxDatatableColumn>,
    options: VisibleColumnOptions
  ): boolean {
    if (column.cellTemplate) return true;

    if (!column.prop && !column.name) return false;

    if (options.excludeEmptyColumns && this.isEmptyColumn(column)) return false;

    return true;
  }

  private evaluateVisibilityCriteria(
    column: Partial<NgxDatatableColumn>,
    options: VisibleColumnOptions
  ): VisibleColumnCriteria {
    const columnName = (column.name || column.prop || '').toLowerCase();

    return {
      hasVisibleProperty: column.visible !== undefined,
      hasValidPropOrName: !!(column.prop || column.name),
      isActionColumn: this.matchesPatterns(columnName, [
        ...VISIBLE_COLUMN_CONFIGS['ACTION'].patterns,
      ]),
      isExpandableColumn: this.matchesPatterns(columnName, [
        ...VISIBLE_COLUMN_CONFIGS['EXPANDABLE'].patterns,
      ]),
      matchesExcludePattern: this.matchesCustomExcludePatterns(
        columnName,
        options.customExcludePatterns
      ),
      isEmpty: this.isEmptyColumn(column),
    };
  }

  private calculateColumnVisibilityScore(
    criteria: VisibleColumnCriteria
  ): number {
    return Object.entries(criteria).reduce((total, [key, value]) => {
      const weight = VISIBLE_CRITERIA_WEIGHTS[key] || 0;
      return total + (value ? weight : 0);
    }, 0);
  }

  private calculateVisibilityConfidence(
    allColumns: Partial<NgxDatatableColumn>[],
    visibleColumns: Partial<NgxDatatableColumn>[],
    options: VisibleColumnOptions
  ): number {
    if (allColumns.length === 0) return 0;

    const visibilityRatio = visibleColumns.length / allColumns.length;
    const hasExplicitVisible = allColumns.some(
      (col) => col.visible !== undefined
    );

    let confidence = visibilityRatio * 0.6;

    if (hasExplicitVisible) confidence += 0.3;
    if (options.respectVisibleProperty) confidence += 0.1;

    return Math.min(confidence, 1.0);
  }

  private matchesCustomExcludePatterns(
    columnName: string,
    patterns: string[]
  ): boolean {
    return patterns.some((pattern) =>
      columnName.includes(pattern.toLowerCase())
    );
  }

  private isEmptyColumn(column: Partial<NgxDatatableColumn>): boolean {
    if (column.cellTemplate) return false;

    if ((column as any).headerTemplate) return false;

    return !column.prop && !column.name;
  }

  private createDefaultVisibleResult(
    columns: Partial<NgxDatatableColumn>[]
  ): VisibleColumnDetectionResult {
    const visibleColumns = columns.filter((col) => col.visible !== false);
    const hiddenColumns = columns.filter((col) => col.visible === false);

    return {
      visibleColumns,
      hiddenColumns,
      strategy: VISIBLE_DETECTION_STRATEGIES['PERMISSIVE'],
      confidence: 0.5,
      totalProcessed: columns.length,
    };
  }

  hasVisibleColumns(
    columns: Partial<NgxDatatableColumn>[],
    options?: Partial<VisibleColumnOptions>
  ): boolean {
    if (!columns?.length) {
      return false;
    }

    const mergedOptions = { ...DEFAULT_VISIBLE_OPTIONS, ...options };

    return !!columns.some((col) => {
      if (col.visible === false) return false;
      if (col.visible === true) return true;
      return this.passesBasicVisibilityChecks(col, mergedOptions);
    });
  }

  getVisibleDetectionSummary(
    columns: Partial<NgxDatatableColumn>[],
    options?: Partial<VisibleColumnOptions>
  ) {
    const result = this.detectVisibleColumns(columns, options);
    const hasVisible = this.hasVisibleColumns(columns, options);

    return {
      hasVisibleColumns: hasVisible,
      strategy: result.strategy,
      confidence: result.confidence,
      visibleCount: result.visibleColumns.length,
      hiddenCount: result.hiddenColumns.length,
      totalProcessed: result.totalProcessed,
      confidenceLevel: this.getConfidenceLevel(result.confidence),
    };
  }

  private matchesPatterns(columnName: string, patterns: string[]): boolean {
    return patterns.some((pattern) =>
      columnName.includes(pattern.toLowerCase())
    );
  }
}
